import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Edit2, Trash2, X, Loader, Eye, Wifi, WifiOff, User, Zap, AlertCircle, CheckCircle2, Clock, MapPin, Phone, ServerCog, ShieldCheck, Radio } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';
const LIMIT = 50;
const ONU_INVENTORY_CACHE_KEY = 'myanmarlink.noc.onu-inventory.v1';
const ONU_INVENTORY_CACHE_TTL_MS = 2 * 60 * 1000;
function readCachedOnuInventory() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(ONU_INVENTORY_CACHE_KEY) || 'null');
    if (!cached || !Array.isArray(cached.items) || Date.now() - Number(cached.savedAt || 0) > ONU_INVENTORY_CACHE_TTL_MS) return null;
    return cached;
  } catch { return null; }
}
function summarizeOnuInventory(items = []) {
  const rows = Array.isArray(items) ? items : [];
  const online = rows.filter(item => String(item?.status || '').toLowerCase() === 'online').length;
  const offline = rows.filter(item => String(item?.status || '').toLowerCase() === 'offline').length;
  const lowSignal = rows.filter(item => item?.signal_rx !== null && item?.signal_rx !== undefined && Number(item.signal_rx) < -27).length;
  const critical = rows.filter(item => item?.signal_rx !== null && item?.signal_rx !== undefined && Number(item.signal_rx) < -30).length;
  const waiting = rows.filter(item => ['unconfigured', 'unauthorized', 'pending', 'waiting', 'new'].includes(String(item?.status || '').toLowerCase())).length;
  return { total_authorized: Math.max(0, rows.length - waiting), waiting_authorization: waiting, online, offline, low_signal: lowSignal, critical_signal: critical, resync: 0 };
}
const LIVE_STATUS_STALE_MS = 90 * 1000;
const LIVE_STATUS_RECHECK_GUARD_MS = 75 * 1000;

const T = {
  bg: '#FAFBFF', surface: '#FFFFFF', surface1: '#F0F1FA', surface2: '#E8E9F5',
  primary: '#0052CC', primaryLight: '#D6E3FF', onPrimary: '#FFFFFF',
  onSurface: '#1A1C1E', onSurfaceVar: '#44474F',
  outline: '#74777F', outlineVar: '#C4C6D0',
  error: '#BA1A1A', errorContainer: '#FFDAD6',
  success: '#006D3B', successContainer: '#E8F5E9',
  warning: '#E65100', warningContainer: '#FFF8E1',
  amber: '#B45309', amberBg: '#FEF3C7',
};

function useApi(token) {
  return useMemo(() => {
    const request = (path, init = {}, timeoutMs = 10000) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      return fetch(`${API}/api${path}`, { ...init, signal: controller.signal })
        .then(async response => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload?.detail || payload?.error || `HTTP ${response.status}`);
          return payload;
        })
        .finally(() => clearTimeout(timer));
    };
    const auth = { Authorization: `Bearer ${token}` };
    return {
      // Inventory and summary reads are bounded so a stalled backend cannot keep the page spinner alive forever.
      get: (path) => request(path, { headers: auth }, path.startsWith('/onus?') ? 8000 : 10000),
      post: (path, body) => request(path, { method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) }, path.includes('/get-status') || path.includes('/live-run-state') ? 30000 : 10000),
      put: (path, body) => request(path, { method: 'PUT', headers: { ...auth, 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, 12000),
      del: (path) => request(path, { method: 'DELETE', headers: auth }, 12000),
    };
  }, [token]);
}

const STATUS_STYLE = {
  online:       { bg: '#E8F5E9', color: '#1B5E20', dot: '#2E7D32', icon: Wifi },
  offline:      { bg: '#FFEBEE', color: '#B71C1C', dot: '#C62828', icon: WifiOff },
  unconfigured: { bg: '#FFF8E1', color: '#E65100', dot: '#F57C00', icon: AlertCircle },
  removing:     { bg: '#E8F0FE', color: '#0052CC', dot: '#0052CC', icon: Loader },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unconfigured;
  const Icon = s.icon;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <Icon size={10} />
      {status || 'unknown'}
    </span>
  );
}

function parseStatusTime(value) {
  if (!value) return null;
  const raw = String(value);
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const date = new Date(/(?:Z|[+-]\d\d:?\d\d)$/.test(normalized) ? normalized : `${normalized}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function statusAgeMs(value) {
  const date = parseStatusTime(value);
  return date ? Math.max(0, Date.now() - date.getTime()) : Number.POSITIVE_INFINITY;
}

function StatusCell({ onu, check }) {
  const status = String(onu.status || 'unknown').toLowerCase();
  const checkedAt = check?.checkedAt || onu.last_seen || onu.updated_at;
  const ageMs = statusAgeMs(checkedAt);
  const isVerifying = check?.state === 'checking';
  const failed = check?.state === 'error';
  const verified = check?.state === 'verified';
  const stale = !isVerifying && !verified && ageMs >= LIVE_STATUS_STALE_MS;
  const color = isVerifying ? T.primary : failed ? T.warning : stale ? T.warning : status === 'online' ? T.success : status === 'offline' ? T.error : T.outline;
  const label = isVerifying ? 'Verifying OLT status…'
    : failed ? 'OLT check unavailable · stored state retained'
      : verified ? `OLT status verified${check?.queryMs ? ` · ${(check.queryMs / 1000).toFixed(1)}s` : ''}`
        : stale ? 'Stored state is stale · automatic OLT check queued'
          : `Last verified ${checkedAt ? parseStatusTime(checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'not recorded'}`;
  return <div style={{ minWidth: 160 }} title={check?.error || label}>
    <StatusBadge status={status} />
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5, color, fontSize: 9, fontWeight: 700, lineHeight: 1.25 }}>
      <span style={{ width: 6, height: 6, flex: '0 0 auto', borderRadius: '50%', background: color, animation: isVerifying ? 'statusPulse 1.1s ease-in-out infinite' : 'none' }} />
      <span>{label}</span>
    </div>
  </div>;
}

function formatElapsed(since) {
  const started = parseStatusTime(since);
  if (!started) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - started.getTime()) / 1000));
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${Math.max(1, minutes)}m`;
}

function AvailabilityCell({ onu }) {
  const status = String(onu.status || '').toLowerCase();
  const since = onu.availability_since;
  const elapsed = formatElapsed(since);
  const baseline = onu.availability_source === 'erp_baseline';
  const online = status === 'online';
  const color = online ? '#16803C' : status === 'offline' ? '#B42318' : '#667085';
  const primary = !since ? 'Duration pending first verified state' : online ? (baseline ? `Observed up · ${elapsed}` : `Up for ${elapsed}`) : (baseline ? 'Offline start not recorded' : `Down for ${elapsed}`);
  const secondary = !since ? 'The next OLT status change starts tracking' : baseline ? `Baseline ${new Date(since).toLocaleString()}` : `Since ${new Date(since).toLocaleString()}`;
  return <div style={{ minWidth: 172 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontWeight: 760, fontSize: 11 }}>
      <Clock size={12} />{primary}
    </div>
    <div style={{ marginTop: 5, color: '#7A8699', fontSize: 10, lineHeight: 1.3 }}>{secondary}</div>
  </div>;
}

function OutageEvidenceCell({ onu }) {
  const evidence = onu.outage_evidence || {};
  const type = evidence.class || 'cause_unconfirmed';
  const tone = type === 'shared_pon_suspected'
    ? { color: '#B54708', bg: '#FFFAEB', border: '#FEDF89' }
    : type === 'optical_path_watch'
      ? { color: '#B54708', bg: '#FFF6DE', border: '#F7DF9F' }
      : type === 'online'
        ? { color: '#16803C', bg: '#ECFDF3', border: '#ABEFC6' }
        : { color: '#475467', bg: '#F2F4F7', border: '#D0D5DD' };
  const isFiberCut = ['optical_path_watch', 'optical_signal_lost', 'optical_link_fault'].includes(type);
  return <div style={{ minWidth: 218 }}>
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: tone.color, background: tone.bg, border: `1px solid ${tone.border}`, borderRadius: 999, padding: '4px 8px', fontWeight: 750, fontSize: 10 }}>
      {isFiberCut ? <img src="/assets/fiber-cut.png" alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6, margin: '-9px 0' }} /> : type === 'online' ? <CheckCircle2 size={11} /> : <AlertCircle size={11} />}{evidence.label || 'Cause not confirmed'}
    </div>
    <div style={{ marginTop: 6, color: '#667085', fontSize: 10, lineHeight: 1.35 }}>{evidence.detail || 'No outage evidence is available yet'}</div>
  </div>;
}

function OltSummaryCard({ tone, Icon, value, label, detail, footer, onClick, active }) {
  return <button type="button" onClick={onClick} aria-pressed={active} title={`Show ${label.toLowerCase()} ONUs`} style={{ borderRadius: 11, overflow: 'hidden', background: active ? tone.tint : '#fff', border: `1px solid ${active ? tone.accent : tone.border}`, boxShadow: active ? `0 0 0 2px ${tone.tint}, 0 2px 5px rgba(16,24,40,.12)` : '0 1px 3px rgba(16,24,40,.07)', minWidth: 0, width: '100%', padding: 0, textAlign: 'left', cursor: 'pointer', transition: 'background .15s ease, border-color .15s ease, box-shadow .15s ease' }}>
    <div style={{ minHeight: 72, padding: '12px 13px 10px', borderTop: `4px solid ${tone.accent}`, display: 'grid', gridTemplateColumns: '36px minmax(0,1fr) auto', alignItems: 'start', gap: 10 }}>
      <div style={{ width: 36, height: 36, marginTop: 1, display: 'grid', placeItems: 'center', borderRadius: 9, color: tone.accent, background: tone.tint }}><Icon size={19} strokeWidth={2.45} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: '#344054', fontSize: 9, fontWeight: 800, lineHeight: 1.3, letterSpacing: '.62px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        <div style={{ marginTop: 5, color: '#7A8699', fontSize: 10, lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{detail}</div>
      </div>
      <div style={{ color: tone.accent, fontSize: 29, lineHeight: .94, fontWeight: 800, letterSpacing: '-.8px', fontVariantNumeric: 'tabular-nums', alignSelf: 'start' }}>{value}</div>
    </div>
    <div style={{ minHeight: 27, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 13px', borderTop: '1px solid #EEF1F4', color: '#667085', fontSize: 9, lineHeight: 1, fontWeight: 750 }}>
      {footer}
    </div>
  </button>;
}

function SpeedBadge({ label }) {
  if (!label) return <span style={{ color: '#74777F' }}>—</span>;
  return (
    <span style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Zap size={9} />
      {label}
    </span>
  );
}

function CustomerCell({ onu }) {
  const customerLabel = onu.customer_name || onu.customer_username || onu.description;
  if (!customerLabel) {
    return <span style={{ color: '#74777F', fontSize: 11, fontStyle: 'italic' }}>Unassigned</span>;
  }
  return (
    <div>
      <div style={{ color: '#1A1C1E', fontSize: 13, fontWeight: 600 }}>{customerLabel}</div>
      {onu.customer_phone && <div style={{ color: '#44474F', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}><Phone size={9} />{onu.customer_phone}</div>}
      {onu.customer_city && <div style={{ color: '#44474F', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}><MapPin size={9} />{onu.customer_city}</div>}
    </div>
  );
}

function opticalHealth(signalRx) {
  const rx = Number(signalRx);
  if (!Number.isFinite(rx) || rx >= 0) return { label: 'Waiting', percent: 0, color: '#74777F', background: '#F0F1FA', detail: 'No valid OLT optical measurement' };
  const percent = Math.max(0, Math.min(100, Math.round(((rx + 28) / 20) * 100)));
  if (rx >= -12) return { label: 'Excellent', percent, color: '#00875A', background: '#DFF7EC', detail: 'Strong optical margin' };
  if (rx >= -20) return { label: 'Good', percent, color: '#0E7490', background: '#DDF6FA', detail: 'Stable optical range' };
  if (rx >= -25) return { label: 'Watch', percent, color: '#B45309', background: '#FEF3C7', detail: 'Monitor attenuation' };
  return { label: 'Weak', percent, color: '#BA1A1A', background: '#FFDAD6', detail: 'Check fibre path' };
}

function OpticalCell({ onu, liveOptical }) {
  const hasFreshResponse = liveOptical?.hasResponse === true;
  const liveRx = hasFreshResponse ? liveOptical.rx : null;
  const liveTx = hasFreshResponse ? liveOptical.tx : null;
  const health = opticalHealth(liveRx);
  const hasRx = Number.isFinite(Number(liveRx)) && Number(liveRx) < 0;
  const hasTx = Number.isFinite(Number(liveTx));
  const staleLabel = !hasFreshResponse ? 'Stored value hidden · no fresh OLT sample' : (!hasRx && !hasTx ? 'Fresh response · optical values unavailable' : (!hasRx ? 'Fresh response · Rx unavailable' : !hasTx ? 'Fresh response · Tx unavailable' : 'Fresh OLT sample'));
  return (
    <div style={{ minWidth: 150 }} title={staleLabel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: hasRx ? T.onSurface : T.outline }}>
        <Radio size={12} color={health.color} />
        <span>Rx {hasRx ? `${Number(liveRx).toFixed(2)}` : '—'} dBm</span>
      </div>
      <div style={{ marginTop: 5, color: hasTx ? T.onSurfaceVar : T.outline, fontSize: 11, fontFamily: 'monospace' }}>Tx {hasTx ? `${Number(liveTx).toFixed(2)} dBm` : '— dBm'}</div>
      <div style={{ marginTop: 4, color: hasFreshResponse && (hasRx || hasTx) ? '#667085' : '#B45309', fontSize: 9, fontWeight: 700, letterSpacing: '.25px' }}>{staleLabel}</div>
    </div>
  );
}

function SignalGauge({ signalRx }) {
  const rx = Number(signalRx);
  const hasReading = Number.isFinite(rx) && rx < 0;
  const state = !hasReading ? { label: 'AWAIT SAMPLE', color: '#64748b', marker: 2 }
    : rx > -8 ? { label: 'HIGH RX', color: '#b45309', marker: 98 }
    : rx >= -27 ? { label: rx >= -18 ? 'EXCELLENT' : 'NOMINAL', color: '#167b5b', marker: Math.max(2, Math.min(98, ((rx + 30) / 22) * 100)) }
    : rx >= -30 ? { label: 'LOW MARGIN', color: '#b45309', marker: Math.max(2, ((rx + 30) / 22) * 100) }
    : { label: 'CRITICAL WEAK', color: '#b42318', marker: 2 };
  return (
    <div title={hasReading ? `Calibrated ONU receive level: ${rx.toFixed(2)} dBm` : 'Awaiting an OLT optical read'} style={{ minWidth: 126 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }}>
        <span style={{ color: '#778596', fontSize: 8, letterSpacing: '.65px', fontWeight: 800 }}>RX CAL.</span>
        <span style={{ color: state.color, fontSize: 9, fontWeight: 800 }}>{hasReading ? `${rx.toFixed(2)} dBm` : '— dBm'}</span>
      </div>
      <div style={{ height: 13, position: 'relative', background: '#f6f8fa', borderTop: '1px solid #cbd5dd', borderBottom: '1px solid #cbd5dd' }}>
        <span style={{ position: 'absolute', left: '14%', top: 0, bottom: 0, borderLeft: '1px solid #d9e0e6' }} />
        <span style={{ position: 'absolute', left: '27%', right: '9%', top: 0, bottom: 0, background: 'rgba(22,123,91,.055)', borderLeft: '1px solid #9cbcaf', borderRight: '1px solid #9cbcaf' }} />
        <i style={{ position: 'absolute', left: `calc(${state.marker}% - 1px)`, top: -4, width: 2, height: 19, background: state.color, boxShadow: `0 0 0 1px #fff,0 0 0 2px ${state.color}`, transition: 'left .35s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, color: '#7a8794', fontSize: 8, fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }}><span>−30</span><span>−27</span><span>−18</span><span>−8</span></div>
      <div style={{ marginTop: 2, color: state.color, fontSize: 8, fontWeight: 800, letterSpacing: '.45px' }}>{state.label} · {liveOptical?.hasResponse === true ? 'FRESH OLT SAMPLE' : 'STORED VALUE HIDDEN'}</div>
    </div>
  );
}

function HealthBar({ signalRx, liveOptical }) {
  const freshRx = liveOptical?.hasResponse === true ? liveOptical.rx : null;
  const health = opticalHealth(freshRx);
  return (
    <div style={{ minWidth: 112 }} title={liveOptical?.hasResponse === true ? health.detail : 'Stored optical value hidden until a fresh OLT sample is available'}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
        <span style={{ color: health.color, fontSize: 11, fontWeight: 750 }}>{liveOptical?.hasResponse === true ? health.label : 'STALE'}</span>
        <span style={{ color: T.onSurfaceVar, fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>{health.percent}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 10, overflow: 'hidden', background: health.background }}>
        <div style={{ width: `${health.percent}%`, height: '100%', borderRadius: 10, background: health.color, transition: 'width 250ms ease' }} />
      </div>
    </div>
  );
}

function SignalTrend({ history, summary }) {
  const samples = Array.isArray(history) ? history.filter(item => Number.isFinite(Number(item?.rx))) : [];
  const trend = summary?.direction || 'collecting';
  const trendStyle = trend === 'improving'
    ? { color: '#00875A', text: 'Improving' }
    : trend === 'declining'
      ? { color: '#BA1A1A', text: 'Falling' }
      : trend === 'stable'
        ? { color: '#0E7490', text: 'Stable' }
        : { color: T.outline, text: 'Collecting' };
  if (samples.length < 2) {
    return <div style={{ minWidth: 108, color: T.outline, fontSize: 11 }} title="Trend is shown only after two verified OLT optical samples are recorded.">
      <div style={{ color: trendStyle.color, fontWeight: 700 }}>{trendStyle.text}</div>
      <div style={{ marginTop: 5 }}>{samples.length ? '1 verified sample' : 'No samples yet'}</div>
    </div>;
  }
  const values = samples.map(item => Number(item.rx));
  const low = Math.min(...values);
  const high = Math.max(...values);
  const range = Math.max(0.8, high - low);
  const points = values.map((value, index) => {
    const x = 4 + (index * 96) / Math.max(1, values.length - 1);
    const y = 25 - ((value - low) / range) * 18;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const delta = Number(summary?.delta_db);
  return (
    <div style={{ minWidth: 108 }} title={`${samples.length} verified OLT samples${Number.isFinite(delta) ? ` · ${delta > 0 ? '+' : ''}${delta.toFixed(2)} dB` : ''}`}>
      <svg viewBox="0 0 104 30" width="104" height="30" role="img" aria-label={`Optical trend: ${trendStyle.text}`}>
        <path d="M4 25 H100" stroke="#E2E8F0" strokeWidth="1" />
        <polyline points={points} fill="none" stroke={trendStyle.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.split(' ').map((point, index) => {
          const [cx, cy] = point.split(',');
          return <circle key={`${cx}-${cy}-${index}`} cx={cx} cy={cy} r={index === samples.length - 1 ? 2.8 : 1.7} fill={trendStyle.color} />;
        })}
      </svg>
      <div style={{ marginTop: 1, color: trendStyle.color, fontSize: 10, fontWeight: 700 }}>{trendStyle.text} · {samples.length} samples</div>
    </div>
  );
}

function EditModal({ onu, onClose, onSave, speedProfiles }) {
  const [form, setForm] = useState({
    description: onu.description || '',
    odb_splitter: onu.odb_splitter || '',
    zone: onu.zone || '',
    pppoe_username: onu.pppoe_username || '',
    customer_username: onu.customer_username || '',
    speed_profile_id: onu.speed_profile_id || '',
  });
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave(onu.id, form); setSaving(false); };
  const inputStyle = { width: '100%', background: T.surface1, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '9px 10px', color: T.onSurface, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
  const f = (label, key, placeholder = '', type = 'text') => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', color: T.onSurfaceVar, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} style={inputStyle} />
    </div>
  );
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 16, width: 460, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h3 style={{ color: T.onSurface, margin: 0, fontWeight: 700 }}>Edit ONU</h3>
            <div style={{ color: T.onSurfaceVar, fontSize: 12, fontFamily: 'monospace', marginTop: 2 }}>{onu.onu_serial}</div>
          </div>
          <button onClick={onClose} style={{ background: T.surface1, border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.onSurfaceVar, cursor: 'pointer' }}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          {f('Description / Customer Name', 'description', 'e.g. U Kyaw Zin - Hlaing')}
          {f('PPPoE Username', 'pppoe_username', 'e.g. mgd001001')}
          {f('Customer Username (Radius)', 'customer_username', 'Link to Radius rm_users')}
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', color: T.onSurfaceVar, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Speed Profile</label>
            <select value={form.speed_profile_id} onChange={e => setForm(f => ({ ...f, speed_profile_id: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">— No profile —</option>
              {(speedProfiles || []).map(sp => (
                <option key={sp.id} value={sp.id}>{sp.name} {sp.download_mbps ? `(${sp.download_mbps}/${sp.upload_mbps} Mbps)` : ''}</option>
              ))}
            </select>
          </div>
          {f('ODB Splitter', 'odb_splitter', 'e.g. ODB-Hlaing-01')}
          {f('Zone', 'zone', 'e.g. Zone-A')}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ background: T.surface1, border: `1px solid ${T.outlineVar}`, borderRadius: 20, padding: '9px 20px', color: T.onSurfaceVar, cursor: 'pointer', fontWeight: 500 }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ background: T.primary, border: 'none', borderRadius: 20, padding: '9px 24px', color: T.onPrimary, cursor: 'pointer', fontWeight: 600 }}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RemovalProgressModal({ job, onClose }) {
  const isDone = ['completed', 'failed', 'not_found'].includes(job.status);
  const isSuccess = job.status === 'completed';
  const stageRows = job.steps || [];
  const progress = isDone ? 100 : Math.min(94, 8 + Math.max(0, stageRows.length - 1) * 14);
  const messageColor = isSuccess ? T.success : ['failed', 'not_found'].includes(job.status) ? T.error : T.primary;
  const message = ['failed', 'not_found'].includes(job.status) ? (job.error || job.step || 'Removal job stopped') : (job.step || 'Preparing verified OLT cleanup...');
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(7,16,35,0.56)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ background: '#FFFFFF', borderRadius: 18, width: 540, maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.28)', overflow: 'hidden' }}>
        <div style={{ padding: '22px 24px 18px', background: isSuccess ? '#EDF8F2' : job.status === 'failed' ? '#FFF1F0' : '#F3F7FF', borderBottom: `1px solid ${T.outlineVar}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: isSuccess ? T.successContainer : job.status === 'failed' ? T.errorContainer : T.primaryLight, color: messageColor }}>
                {isSuccess ? <ShieldCheck size={21} /> : job.status === 'failed' ? <AlertCircle size={21} /> : <ServerCog size={21} />}
              </div>
              <div>
                <div style={{ color: T.onSurface, fontSize: 16, fontWeight: 750 }}>{isSuccess ? 'ONU removal verified' : job.status === 'failed' ? 'ONU removal stopped' : 'Removing ONU safely'}</div>
                <div style={{ color: T.onSurfaceVar, fontSize: 12, marginTop: 3, fontFamily: 'monospace' }}>{job.serial || 'ONU'} · {job.job_id || 'starting'}</div>
              </div>
            </div>
            {isDone && <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: T.onSurfaceVar, cursor: 'pointer', padding: 5 }} title="Close"><X size={19} /></button>}
          </div>
        </div>
        <div style={{ padding: '20px 24px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 12 }}>
            <span style={{ color: messageColor, fontWeight: 700 }}>{message}</span>
            <span style={{ color: T.onSurfaceVar, fontVariantNumeric: 'tabular-nums' }}>{Number(job.elapsed || 0).toFixed(1)}s</span>
          </div>
          <div style={{ height: 8, background: T.surface2, borderRadius: 99, overflow: 'hidden', marginBottom: 18 }}>
            <div style={{ width: `${progress}%`, height: '100%', borderRadius: 99, transition: 'width 0.35s ease', background: isSuccess ? T.success : job.status === 'failed' ? T.error : T.primary }} />
          </div>
          <div style={{ border: `1px solid ${T.outlineVar}`, borderRadius: 10, overflow: 'hidden', maxHeight: 210, overflowY: 'auto' }}>
            {stageRows.length ? stageRows.map((item, index) => (
              <div key={`${item.stage || item.step}-${index}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderBottom: index < stageRows.length - 1 ? `1px solid ${T.surface2}` : 'none' }}>
                <div style={{ width: 18, paddingTop: 1, color: item.stage === 'failed' ? T.error : item.stage === 'complete' ? T.success : T.primary }}>
                  {item.stage === 'complete' ? <CheckCircle2 size={15} /> : item.stage === 'failed' ? <AlertCircle size={15} /> : <Loader size={15} style={{ animation: item.stage === job.stage && !isDone ? 'spin 1s linear infinite' : 'none' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: T.onSurface, fontSize: 12, fontWeight: 600 }}>{item.step}</div>
                  <div style={{ color: T.outline, fontSize: 10, marginTop: 2, fontFamily: 'monospace' }}>{item.stage || 'OLT'} · {Number(item.elapsed || 0).toFixed(1)}s</div>
                </div>
              </div>
            )) : <div style={{ padding: 14, color: T.onSurfaceVar, fontSize: 12 }}>Creating cleanup job...</div>}
          </div>
          {isDone && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}><button onClick={onClose} style={{ background: isSuccess ? T.success : T.error, color: '#fff', border: 'none', borderRadius: 20, padding: '9px 20px', cursor: 'pointer', fontWeight: 700 }}>Close</button></div>}
        </div>
      </div>
    </div>
  );
}

export default function NOCONUPage({ token: tokenProp }) {
  const token = tokenProp || localStorage.getItem('session_token') || '';
  const api = useApi(token);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [olts, setOlts] = useState([]);
  const initialInventoryCache = readCachedOnuInventory();
  const initialCachedItems = initialInventoryCache?.items || [];
  const [onus, setOnus] = useState(() => initialCachedItems);
  const [total, setTotal] = useState(() => initialInventoryCache?.total || 0);
  const [summary, setSummary] = useState(() => summarizeOnuInventory(initialCachedItems));
  const [oltOnlyGroups, setOltOnlyGroups] = useState([]);
  const [oltOnlyGroupsMeta, setOltOnlyGroupsMeta] = useState({ total: 0, vlan_available: false });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [filterOlt, setFilterOlt] = useState(() => searchParams.get('olt_id') || '');
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || '');
  const [summaryFilter, setSummaryFilter] = useState(() => {
    const signalFilter = searchParams.get('signal_filter');
    const statusFilter = searchParams.get('status');
    if (signalFilter === 'low' || signalFilter === 'warning' || signalFilter === 'offline_power_fail' || signalFilter === 'offline_los' || signalFilter === 'offline_na' || signalFilter === 'waiting_deleted' || signalFilter === 'waiting_resync' || signalFilter === 'waiting_new' || signalFilter === 'online_verified') return signalFilter;
    if (statusFilter === 'unconfigured') return 'waiting';
    return 'all';
  });
  const [showOltOnlyGroups, setShowOltOnlyGroups] = useState(true);
  const [search, setSearch] = useState('');
  const [editOnu, setEditOnu] = useState(null);
  const [toast, setToast] = useState(null);
  const [speedProfiles, setSpeedProfiles] = useState([]);
  const [removeJob, setRemoveJob] = useState(null);
  const [statusChecks, setStatusChecks] = useState({});
  const [liveOptical, setLiveOptical] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  // dashboard-filter-query: initial filters are read from OLT/ONU Dashboard links.
  const statusCheckGuardRef = useRef(new Map());
  const loadOnusInFlightRef = useRef(null);
  const hasLoadedOnusRef = useRef(Boolean(initialInventoryCache));

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadOlts = useCallback(async () => {
    try { const d = await api.get('/noc/olts'); setOlts(Array.isArray(d) ? d : []); } catch {}
  }, [api]);

  const loadSpeedProfiles = useCallback(async () => {
    try { const d = await api.get('/noc/speed-profiles'); setSpeedProfiles(Array.isArray(d) ? d : []); } catch {}
  }, [api]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await api.get('/noc/onus/summary');
      if (data && typeof data === 'object') setSummary(current => ({ ...current, ...data }));
    } catch {}
  }, [api]);
  const loadOltOnlyGroups = useCallback(async () => {
    try {
      const data = await api.get('/noc/onu-olt-only-groups');
      setOltOnlyGroups(Array.isArray(data?.groups) ? data.groups : []);
      setOltOnlyGroupsMeta({ total: Number(data?.total || 0), vlan_available: Boolean(data?.vlan_available) });
    } catch { setOltOnlyGroups([]); setOltOnlyGroupsMeta({ total: 0, vlan_available: false }); }
  }, [api]);

  const loadOnus = useCallback(async () => {
    if (loadOnusInFlightRef.current) return loadOnusInFlightRef.current;
    const isInitialLoad = !hasLoadedOnusRef.current;
    if (isInitialLoad) setLoading(true);
    else setRefreshing(true);
    const run = (async () => {
      try {
        const params = new URLSearchParams({ page, limit: LIMIT });
      if (filterOlt) params.set('olt_id', filterOlt);
      if (filterStatus) params.set('status', filterStatus);
      if (summaryFilter === 'low' || summaryFilter === 'warning' || summaryFilter === 'offline_power_fail' || summaryFilter === 'offline_los' || summaryFilter === 'offline_na' || summaryFilter === 'waiting_deleted' || summaryFilter === 'waiting_resync' || summaryFilter === 'waiting_new' || summaryFilter === 'online_verified' || summaryFilter === 'olt_online_unassigned') params.set('signal_filter', summaryFilter);
      if (search) params.set('search', search);
      // ONU ID 0 is valid on zero-based OLTs; do not use ont_id as authorization truth.
      const data = await api.get(`/noc/onus?${params}`);
      let items = Array.isArray(data?.items) ? data.items : [];
        // Keep the stale-value safeguard, but restore a sample that was freshly
        // verified by the read-only OLT worker before this page was reloaded.
        // A row remains STALE unless it has both a recent verification timestamp
        // and at least one persisted optical field from that live worker.
        const hydratedOptical = {};
        items.forEach((onu) => {
          const hasOptical = onu.signal_rx !== null && onu.signal_rx !== undefined
            || onu.signal_tx !== null && onu.signal_tx !== undefined;
          if (hasOptical && statusAgeMs(onu.last_seen || onu.updated_at) < LIVE_STATUS_STALE_MS) {
            hydratedOptical[onu.id] = {
              hasResponse: true,
              rx: onu.signal_rx ?? null,
              tx: onu.signal_tx ?? null,
              checkedAt: onu.last_seen || onu.updated_at,
              source: 'persisted-fresh-olt-sample',
            };
          }
        });
        if (Object.keys(hydratedOptical).length) {
          setLiveOptical(current => ({ ...current, ...hydratedOptical }));
        }
        setOnus(items);
        setTotal(data?.total || 0);
        try {
          if (page === 1 && !filterOlt && !filterStatus && summaryFilter === 'all' && !search) {
            sessionStorage.setItem(ONU_INVENTORY_CACHE_KEY, JSON.stringify({ items, total: data?.total || 0, savedAt: Date.now() }));
          }
        } catch {}
        hasLoadedOnusRef.current = true;
      } catch(e) {
        console.error('Failed to load ONUs:', e);
        // Do not blank a previously rendered inventory when a refresh times out.
        if (isInitialLoad) setOnus([]);
        if (isInitialLoad) showToast('ONU inventory is taking too long to respond; try Refresh again', 'error');
      } finally {
        if (isInitialLoad) setLoading(false);
        setRefreshing(false);
      }
    })();
    loadOnusInFlightRef.current = run;
    try { return await run; }
    finally { if (loadOnusInFlightRef.current === run) loadOnusInFlightRef.current = null; }
  }, [api, page, filterOlt, filterStatus, summaryFilter, search]);

  const openOnuDetail = useCallback((onu) => {
    if (!onu?.id || detailLoadingId) return;
    // Navigation must not wait for live OLT commands. The list row already contains
    // the last verified metadata and optical sample, so hand that snapshot to the
    // detail page immediately. The detail page stages fresh read-only telemetry.
    setDetailLoadingId(onu.id);
    try {
      navigate(`/noc-onu/${onu.id}`, {
        state: {
          prefetchedOnu: { ...onu },
          prefetchedLiveStatus: {
            success: true,
            status: onu.status,
            signal_rx: onu.signal_rx ?? null,
            signal_tx: onu.signal_tx ?? null,
            source: 'stored-inventory-snapshot',
            cached: true,
          },
          prefetchedPhysicalLinks: [],
          prefetchedAt: Date.now(),
          prefetchedNeedsLiveRefresh: true,
        },
      });
    } catch (error) {
      setDetailLoadingId(null);
      showToast('Failed to open ONU detail', 'error');
    }
  }, [detailLoadingId, navigate]);
  const verifyLiveStatus = useCallback(async (onu) => {
    if (!onu?.id) return;
    setStatusChecks(current => ({ ...current, [onu.id]: { state: 'checking', checkedAt: onu.last_seen || null } }));
    try {
      const result = await api.post(`/noc/onus/${onu.id}/live-run-state`);
      if (!result?.success) throw new Error(result?.error || 'OLT did not return a verified run state');
      const checkedAt = result.checked_at || new Date().toISOString();
      setStatusChecks(current => ({ ...current, [onu.id]: { state: 'verified', checkedAt, queryMs: result.query_ms, source: result.source, cached: Boolean(result.cached) } }));
      setLiveOptical(current => ({ ...current, [onu.id]: { hasResponse: true, rx: result.signal_rx ?? null, tx: result.signal_tx ?? null, checkedAt, source: result.source || 'live-run-state' } }));
      setOnus(current => current.map(item => item.id === onu.id ? {
        ...item,
        status: result.status || item.status,
        // Preserve the last known optical reading when a status response does
        // not include a fresh measurement; never blank a known signal.
        signal_rx: result.signal_rx !== null && result.signal_rx !== undefined ? result.signal_rx : item.signal_rx,
        signal_tx: result.signal_tx !== null && result.signal_tx !== undefined ? result.signal_tx : item.signal_tx,
        last_seen: checkedAt,
        updated_at: checkedAt
      } : item));
    } catch (error) {
      setStatusChecks(current => ({ ...current, [onu.id]: { state: 'error', checkedAt: onu.last_seen || null, error: error?.message || 'Live OLT verification failed' } }));
    }
  }, [api]);

  const bulkRefreshOlt = useCallback(async () => {
    const oltId = filterOlt || (olts.length === 1 ? olts[0].id : null);
    if (!oltId) { showToast('Select an OLT first for bulk refresh', 'error'); return; }
    setRefreshing(true);
    try {
      const result = await api.post(`/noc/olts/${oltId}/scan-onus-bulk`, { force_refresh: true });
      if (result?.status === 'failed') throw new Error(result.error || 'Bulk scan failed');
      showToast(`OLT bulk scan complete: ${result.seen ?? 0} ONU records updated`);
      await loadOnus(); await loadSummary();
    } catch (error) { showToast(error?.message || 'OLT bulk scan failed', 'error'); }
    finally { setRefreshing(false); }
  }, [api, filterOlt, olts, loadOnus, loadSummary]);
  useEffect(() => { loadOlts(); loadSpeedProfiles(); loadSummary(); }, [loadOlts, loadSpeedProfiles, loadSummary]);
  useEffect(() => { setPage(1); }, [filterOlt, filterStatus, search]);
  useEffect(() => { loadOnus(); }, [loadOnus]);
  useEffect(() => { if (summaryFilter === 'olt_online_unassigned') loadOltOnlyGroups(); }, [summaryFilter, loadOltOnlyGroups]);
  useEffect(() => {
    const timer = setInterval(() => { loadOnus(); loadSummary(); }, 30000);
    return () => clearInterval(timer);
  }, [loadOnus, loadSummary]);
  useEffect(() => {
    if (!onus.length) return undefined;
    const now = Date.now();
    // Do not mark rows as queued until the actual request starts. Updating one
    // row triggers a list re-render; the former cleanup cancelled the entire
    // batch after its first ONU, leaving the rest stuck as STALE.
    const staleOnus = onus.filter(onu => {
      if (!onu?.id || Number(onu.ont_id) <= 0 || statusAgeMs(onu.last_seen || onu.updated_at) < LIVE_STATUS_STALE_MS) return false;
      const lastQueuedAt = statusCheckGuardRef.current.get(onu.id) || 0;
      return now - lastQueuedAt >= LIVE_STATUS_RECHECK_GUARD_MS;
    }).slice(0, 8);
    if (!staleOnus.length) return undefined;
    let disposed = false;
    const run = async () => {
      for (const onu of staleOnus) {
        if (disposed) break;
        statusCheckGuardRef.current.set(onu.id, Date.now());
        await verifyLiveStatus(onu);
        await new Promise(resolve => setTimeout(resolve, 650));
      }
    };
    run();
    return () => { disposed = true; };
  }, [onus, verifyLiveStatus]);
  const handleSave = async (id, form) => {
    const original = editOnu || {};
    const linkChanged = ['pppoe_username', 'customer_username', 'dma_service_id', 'speed_profile_id', 'zone', 'odb_splitter']
      .some(key => String(original[key] ?? '') !== String(form[key] ?? ''));
    await api.put(`/noc/onus/${id}`, { ...form, speed_profile_id: form.speed_profile_id || null });
    showToast('ONU updated successfully');
    setEditOnu(null);
    loadOnus();
    if (linkChanged) {
      const shouldResync = window.confirm('ONU/PPPoE link ပြောင်းထားပါတယ်။ သက်ဆိုင်ရာ OLT ကို autoresync လုပ်မလား?\n\nYes = bulk read-only sync\nNo = link update ကိုပဲ သိမ်းထားမယ်');
      if (shouldResync && original.olt_id) {
        try {
          await api.post(`/noc/olts/${original.olt_id}/sync`, { method: 'telnet', reason: 'onu_link_changed' });
          showToast('OLT autoresync completed ✓');
          loadOnus();
        } catch (error) { showToast(error.message || 'OLT autoresync failed', 'error'); }
      }
    }
  };

  const handleDelete = async (onu) => {
    if (removeJob && !['completed', 'failed', 'not_found'].includes(removeJob.status)) return;
    if (!window.confirm(`Remove ONU "${onu.onu_serial}" from ERP and OLT?\n\nThe ERP record will be deleted only after the OLT service-port and ONT removal are verified.`)) return;
    try {
      setRemoveJob({ status: 'queued', serial: onu.onu_serial, job_id: 'starting', step: 'Creating verified removal job...', steps: [], elapsed: 0 });
      const r = await api.post(`/noc/onus/${onu.id}/remove-async`);
      if (!r.success || !r.job_id) {
        setRemoveJob({ status: 'failed', serial: onu.onu_serial, job_id: 'not-created', step: r.error || r.detail || 'Unable to start removal job', error: r.error || r.detail, steps: [], elapsed: 0 });
        return;
      }
      setOnus(current => current.map(item => item.id === onu.id ? { ...item, status: 'removing' } : item));
      let finalStatus = null;
      for (let attempt = 0; attempt < 240; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 850));
        const status = await api.get(`/noc/jobs/${r.job_id}/status`);
        const next = { ...status, job_id: r.job_id, serial: onu.onu_serial };
        setRemoveJob(next);
        if (['completed', 'failed', 'not_found'].includes(status.status)) {
          finalStatus = status;
          break;
        }
      }
      if (finalStatus?.status === 'completed') {
        showToast('ONU removal verified and completed');
        loadOnus();
      } else if (finalStatus?.status !== 'failed') {
        setRemoveJob(current => ({ ...current, status: 'failed', error: 'Removal status polling timed out. The ERP record was retained until cleanup can be verified.', step: 'Removal status polling timed out.' }));
      } else {
        showToast(finalStatus.error || 'OLT cleanup failed; ERP record kept', 'error');
        loadOnus();
      }
    } catch (error) {
      setRemoveJob({ status: 'failed', serial: onu.onu_serial, job_id: 'error', step: 'Unable to monitor removal job.', error: error?.message || 'Network error', steps: [], elapsed: 0 });
      showToast('Unable to start or monitor removal job', 'error');
    }
  };

  const applySummaryFilter = (next) => {
    setSummaryFilter(next);
    setFilterStatus(next === 'offline' || next === 'offline_power_fail' || next === 'offline_los' || next === 'offline_na' ? 'offline' : next === 'waiting' ? 'unconfigured' : '');
    setPage(1);
  };

  const totalPages = Math.ceil(total / LIMIT);
  const onlineCount = Number(summary.online ?? onus.filter(o => o.status === 'online').length);
  const offlineCount = Number(summary.offline ?? onus.filter(o => o.status === 'offline').length);
  const lowSignalCount = Number(summary.low_signal || 0);
  const criticalSignalCount = Number(summary.critical_signal || 0);
  const totalAuthorizedCount = Number(summary.total_authorized ?? Math.max(0, total - Number(summary.waiting_authorization || 0)));
  const waitingAuthorizationCount = Number(summary.waiting_authorization || 0);
  const resyncCount = Number(summary.resync || 0);
  const visibleOnus = onus;

  const selectStyle = {
    background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8,
    padding: '9px 12px', color: T.onSurface, fontSize: 13, cursor: 'pointer'
  };

  const tabStyle = (active) => ({
    padding: '8px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: 13, transition: 'all 0.15s',
    background: active ? T.primary : T.surface1,
    color: active ? T.onPrimary : T.onSurfaceVar,
    boxShadow: active ? '0 2px 8px rgba(0,82,204,0.25)' : 'none',
  });

  const thStyle = {
    padding: '11px 14px', color: T.onSurfaceVar, fontSize: 11, fontWeight: 700,
    textAlign: 'left', borderBottom: `1px solid ${T.outlineVar}`,
    whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.04em',
    background: T.surface1,
  };

  return (
    <div style={{ padding: 24, background: T.bg, minHeight: '100vh', fontFamily: 'Roboto, Inter, sans-serif' }}>
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: toast.type === 'error' ? T.error : T.success, color: '#fff', padding: '12px 20px', borderRadius: 10, zIndex: 2000, fontWeight: 600, fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
          {toast.type === 'error' ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ color: '#182230', margin: 0, fontSize: 21, fontWeight: 760, letterSpacing: '-.35px' }}>ONU Management</h1>
          <p style={{ color: '#667085', margin: '4px 0 0', fontSize: 12 }}>Authorization, connection status, and optical overview</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={loadOnus} disabled={loading || refreshing} style={{ background: '#fff', border: '1px solid #D0D5DD', borderRadius: 9, padding: '9px 14px', color: '#344054', cursor: loading || refreshing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, boxShadow: '0 1px 2px rgba(16,24,40,.04)', opacity: loading || refreshing ? .72 : 1 }}>
            <RefreshCw size={14} style={loading || refreshing ? { animation: 'spin 1s linear infinite' } : undefined} /> {loading ? 'Loading…' : refreshing ? 'Refreshing…' : 'Refresh list'}
          </button>
          <button onClick={bulkRefreshOlt} disabled={loading || refreshing} style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 14px', cursor: loading || refreshing ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700 }}>Bulk OLT Refresh</button>
        </div>
      </div>

      {/* Operational summary */}
      <div style={{ background: '#F7F9FC', border: '1px solid #E6EAF0', borderRadius: 14, padding: 8, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
          <OltSummaryCard tone={{ accent: '#0876C9', tint: '#E8F3FF', border: '#C8E3FA' }} Icon={Zap} value={waitingAuthorizationCount} label="WAITING AUTHORIZATION" detail="Click to show new or unconfigured ONUs" active={summaryFilter === 'waiting' || summaryFilter === 'waiting_deleted' || summaryFilter === 'waiting_resync' || summaryFilter === 'waiting_new'} onClick={() => applySummaryFilter(summaryFilter === 'waiting' || summaryFilter === 'waiting_deleted' || summaryFilter === 'waiting_resync' || summaryFilter === 'waiting_new' ? 'all' : 'waiting')} footer={<><span role="button" tabIndex={0} title="Show deleted or removed ONU records" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'waiting_deleted' ? 'all' : 'waiting_deleted'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'waiting_deleted' ? 'underline' : 'none', fontWeight: summaryFilter === 'waiting_deleted' ? 800 : 500 }}>D: {Number(summary.waiting_deleted || 0)}</span><span role="button" tabIndex={0} title="Show waiting ONUs needing resynchronization" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'waiting_resync' ? 'all' : 'waiting_resync'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'waiting_resync' ? 'underline' : 'none', fontWeight: summaryFilter === 'waiting_resync' ? 800 : 500 }}>Resync: {Number(summary.waiting_resync || 0)}</span><span role="button" tabIndex={0} title="Show waiting ONUs created in the last 24 hours" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'waiting_new' ? 'all' : 'waiting_new'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'waiting_new' ? 'underline' : 'none', fontWeight: summaryFilter === 'waiting_new' ? 800 : 500 }}>New: {Number(summary.waiting_new || 0)}</span></>} />
          <OltSummaryCard tone={{ accent: '#16B52A', tint: '#EAFBEA', border: '#C9EFCB' }} Icon={Wifi} value={Number(summary.olt_online_no_pppoe || 0)} label="OLT ONLY" detail="Fresh OLT scan + valid optical RX, without PPPoE/RADIUS assignment" active={summaryFilter === 'olt_online_unassigned'} onClick={() => applySummaryFilter(summaryFilter === 'olt_online_unassigned' ? 'all' : 'olt_online_unassigned')} footer={<><span>Total linked: {totalAuthorizedCount}</span><span role="button" tabIndex={0} title="Show service-usable online ONUs" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'online_verified' ? 'all' : 'online_verified'); }} style={{ cursor: 'pointer', fontWeight: summaryFilter === 'online_verified' ? 800 : 500, textDecoration: summaryFilter === 'online_verified' ? 'underline' : 'none' }}>Online users: {onlineCount}</span></>} />
          <OltSummaryCard tone={{ accent: '#4B4D50', tint: '#F1F2F3', border: '#D9DBDE' }} Icon={WifiOff} value={offlineCount} label="TOTAL OFFLINE" detail="Click to show offline ONUs" active={summaryFilter === 'offline' || summaryFilter === 'offline_power_fail' || summaryFilter === 'offline_los' || summaryFilter === 'offline_na'} onClick={() => applySummaryFilter(summaryFilter === 'offline' || summaryFilter === 'offline_power_fail' || summaryFilter === 'offline_los' || summaryFilter === 'offline_na' ? 'all' : 'offline')} footer={<><span role="button" tabIndex={0} title="Show offline ONUs with confirmed PwrFail / Dying-gasp evidence" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'offline_power_fail' ? 'all' : 'offline_power_fail'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'offline_power_fail' ? 'underline' : 'none', fontWeight: summaryFilter === 'offline_power_fail' ? 800 : 500 }}>PwrFail: {Number(summary.offline_power_fail || 0)}</span><span role="button" tabIndex={0} title="Show offline ONUs with confirmed LoS-family evidence" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'offline_los' ? 'all' : 'offline_los'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'offline_los' ? 'underline' : 'none', fontWeight: summaryFilter === 'offline_los' ? 800 : 500 }}>LoS: {Number(summary.offline_los || 0)}</span><span role="button" tabIndex={0} title="Show offline ONUs without confirmed cause evidence" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'offline_na' ? 'all' : 'offline_na'); }} style={{ cursor: 'pointer', textDecoration: summaryFilter === 'offline_na' ? 'underline' : 'none', fontWeight: summaryFilter === 'offline_na' ? 800 : 500 }}>N/A: {Number(summary.offline_na || 0)}</span></>} />
          <OltSummaryCard tone={{ accent: '#F2A400', tint: '#FFF7E1', border: '#F5D88A' }} Icon={AlertCircle} value={lowSignalCount} label="LOW SIGNALS" detail="Click to show Rx below −27 dBm" active={summaryFilter === 'low' || summaryFilter === 'warning'} onClick={() => applySummaryFilter(summaryFilter === 'low' ? 'all' : 'low')} footer={<><span role="button" tabIndex={0} title="Show warning-level ONUs only (−30 to −27 dBm)" onClick={(event) => { event.stopPropagation(); applySummaryFilter(summaryFilter === 'warning' ? 'all' : 'warning'); }} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); applySummaryFilter(summaryFilter === 'warning' ? 'all' : 'warning'); } }} style={{ cursor: 'pointer', color: summaryFilter === 'warning' ? '#9A3412' : '#667085', textDecoration: summaryFilter === 'warning' ? 'underline' : 'none' }}>Warning: {Math.max(0, lowSignalCount - criticalSignalCount)}</span><span>{summaryFilter === 'warning' ? 'Selected' : summaryFilter === 'low' ? 'Selected' : 'Open'}</span></>} />
        </div>
      </div>

      {summaryFilter === 'olt_online_unassigned' && showOltOnlyGroups && (
        <div style={{ background: '#fff', border: '1px solid #D0D5DD', borderRadius: 12, padding: 14, marginBottom: 16, boxShadow: '0 1px 2px rgba(16,24,40,.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <div><div style={{ color: '#182230', fontSize: 14, fontWeight: 760 }}>OLT Online / No PPPoE — Port Groups</div><div style={{ color: '#667085', fontSize: 11, marginTop: 3 }}>Fresh SNMP bulk evidence only · grouped by OLT and Frame/Slot/PON Port</div></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ background: '#EAFBEA', color: '#16803C', borderRadius: 999, padding: '5px 9px', fontSize: 10, fontWeight: 750 }}>{oltOnlyGroupsMeta.total} ONU</span><button type="button" onClick={() => setShowOltOnlyGroups(false)} style={{ background: '#fff', color: '#475467', border: '1px solid #D0D5DD', borderRadius: 8, padding: '6px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Hide</button></div>
          </div>
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}><thead><tr>{['OLT','Frame / Slot / PON Port','VLAN','ONU count','Last scan'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 9px', color: '#667085', borderBottom: '1px solid #EAECF0', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>)}</tr></thead><tbody>{oltOnlyGroups.map((g, i) => <tr key={`${g.olt_id}-${g.frame_slot_port}-${g.vlan_id}-${i}`} onClick={() => { setFilterOlt(String(g.olt_id)); setSummaryFilter('olt_online_unassigned'); window.setTimeout(() => document.querySelector('[data-onu-inventory]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); }} style={{ cursor: 'pointer' }} title={`Show ${g.olt_name || `OLT-${g.olt_id}`} ONU inventory`}><td style={{ padding: '8px 9px', fontWeight: 700, color: '#344054', borderBottom: '1px solid #F2F4F7' }}>{g.olt_name || `OLT-${g.olt_id}`}</td><td style={{ padding: '8px 9px', color: '#344054', borderBottom: '1px solid #F2F4F7' }}>{g.frame_slot_port || 'N/A'}</td><td style={{ padding: '8px 9px', color: '#667085', borderBottom: '1px solid #F2F4F7' }}>{g.vlan_id ?? 'N/A — not supplied by OLT snapshot'}</td><td style={{ padding: '8px 9px', fontWeight: 800, color: '#0876C9', borderBottom: '1px solid #F2F4F7' }}>{g.onu_count}</td><td style={{ padding: '8px 9px', color: '#667085', borderBottom: '1px solid #F2F4F7' }}>{g.last_scan_at ? new Date(g.last_scan_at).toLocaleString() : 'N/A'}</td></tr>)}{!oltOnlyGroups.length && <tr><td colSpan={5} style={{ padding: 18, color: '#667085', textAlign: 'center' }}>No fresh OLT-only evidence in the 15-minute window. Run Bulk OLT Refresh for a selected OLT.</td></tr>}</tbody></table></div>
          {!oltOnlyGroupsMeta.vlan_available && <div style={{ marginTop: 9, color: '#667085', fontSize: 11 }}>VLAN data မပါလာသေးပါ။ OLT bulk snapshot မှ VLAN ကို အမှန်တကယ်ပြန်ပေးသည့်အခါမှသာ VLAN number ကို ပြသမည်; မခန့်မှန်းပါ။</div>}
        </div>
      )}
      {summaryFilter === 'olt_online_unassigned' && !showOltOnlyGroups && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fff', border: '1px solid #D0D5DD', borderRadius: 10, padding: '9px 12px', marginBottom: 16 }}>
          <div style={{ color: '#344054', fontSize: 12, fontWeight: 700 }}>OLT Online / No PPPoE — Port Groups hidden</div>
          <button type="button" onClick={() => setShowOltOnlyGroups(true)} style={{ background: '#0F766E', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 11px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Show</button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 0 12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: '#182230', fontSize: 14, fontWeight: 760 }}>Authorized ONU inventory</div>
          <div style={{ marginTop: 3, color: '#667085', fontSize: 11 }}>Only added ONUs with an assigned ONT ID are shown. Availability starts tracking from verified bulk OLT snapshots. List refresh never calls the OLT. Use Bulk OLT Refresh for an OLT/PON-level scan.{refreshing ? ' Updating in the background…' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ background: '#ECFDF3', border: '1px solid #ABEFC6', color: '#16803C', borderRadius: 999, padding: '5px 9px', fontSize: 10, fontWeight: 750 }}>Online {onlineCount}</span>
          <span style={{ background: '#FEF3F2', border: '1px solid #FECDCA', color: '#B42318', borderRadius: 999, padding: '5px 9px', fontSize: 10, fontWeight: 750 }}>Offline {offlineCount}</span>
          <span style={{ background: '#F2F4F7', border: '1px solid #D0D5DD', color: '#475467', borderRadius: 999, padding: '5px 9px', fontSize: 10, fontWeight: 750 }}>Total {totalAuthorizedCount}</span>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.outline }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search SN, PPPoE, customer, description..."
            style={{ width: '100%', background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '9px 10px 9px 32px', color: T.onSurface, fontSize: 13, boxSizing: 'border-box' }} />
        </div>
        <select value={filterOlt} onChange={e => setFilterOlt(e.target.value)} style={selectStyle}>
          <option value="">All OLTs</option>
          {olts.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => { setSummaryFilter('all'); setFilterStatus(e.target.value); }} style={selectStyle}>
          <option value="">All Status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
      </div>

      {/* Table */}
      <div data-onu-inventory style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <table style={{ width: '100%', minWidth: 1520, borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr>
              {['Status','ONU / OLT','Access','Optical Rx / Tx','Signal Health','Availability','Outage Evidence','Service / Customer','PPPoE','Actions'].map(h => (
                <th key={h} style={h === 'Actions' ? { ...thStyle, position: 'sticky', right: 0, zIndex: 3, textAlign: 'center', minWidth: 118, boxShadow: '-7px 0 12px rgba(26,28,30,0.08)' } : thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: T.onSurfaceVar }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite', color: T.primary }} />
              </td></tr>
            ) : visibleOnus.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: 'center', padding: 60, color: T.onSurfaceVar, fontSize: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 40 }}>📡</div>
                  <div style={{ fontWeight: 600, color: T.onSurface }}>No authorized ONUs match this inventory filter</div>
                  <div style={{ color: T.onSurfaceVar, fontSize: 13 }}>ONUs appear here only after an ONT ID is assigned during authorization.</div>
                  <button onClick={() => navigate('/noc-onu-auth')} style={{ background: T.primary, color: '#fff', border: 'none', borderRadius: 20, padding: '10px 24px', fontWeight: 600, fontSize: 13, cursor: 'pointer', marginTop: 4 }}>→ Open ONU Authorization</button>
                </div>
              </td></tr>
            ) : visibleOnus.map(onu => (
              <tr key={onu.id} onClick={() => openOnuDetail(onu)} style={{ borderBottom: `1px solid ${T.outlineVar}`, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = T.surface1}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '10px 14px' }}><StatusCell onu={onu} check={statusChecks[onu.id]} /></td>
                <td style={{ padding: '10px 14px', minWidth: 188 }}>
                  <div style={{ color: T.onSurface, fontSize: 12, fontFamily: 'monospace', fontWeight: 750 }}>{onu.onu_serial}</div>
                  <div style={{ color: T.outline, fontSize: 11, marginTop: 4 }}>{onu.olt_name || onu.olt_id}</div>
                </td>
                <td style={{ padding: '10px 14px', minWidth: 108, color: T.onSurfaceVar, fontSize: 12, fontFamily: 'monospace' }}>
                  <div>{onu.frame_slot_port || '—'}</div>
                  <div style={{ color: T.outline, marginTop: 4 }}>ONT {onu.ont_id || '—'} · VLAN {onu.vlan_id || '—'}</div>
                </td>
                <td style={{ padding: '10px 14px' }}><OpticalCell onu={onu} liveOptical={liveOptical[onu.id]} /></td>
                <td style={{ padding: '10px 14px' }}><HealthBar signalRx={onu.signal_rx} liveOptical={liveOptical[onu.id]} /></td>
                <td style={{ padding: '10px 14px' }}><AvailabilityCell onu={onu} /></td>
                <td style={{ padding: '10px 14px' }}><OutageEvidenceCell onu={onu} /></td>
                <td style={{ padding: '10px 14px', minWidth: 164 }}>
                  <SpeedBadge label={onu.speed_label} />
                  <div style={{ marginTop: 7 }}><CustomerCell onu={onu} /></div>
                </td>
                <td style={{ padding: '10px 14px', color: T.onSurfaceVar, fontSize: 13 }}>{onu.pppoe_username || (onu.is_bridge_mode ? <span style={{ color: T.outline, fontStyle: 'italic' }}>Downstream bridge</span> : <span style={{ color: T.outline }}>—</span>)}</td>
                <td style={{ padding: '10px 14px', position: 'sticky', right: 0, zIndex: 2, background: T.surface, minWidth: 118, boxShadow: '-7px 0 12px rgba(26,28,30,0.06)' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={(event) => { event.stopPropagation(); openOnuDetail(onu); }} disabled={detailLoadingId !== null} style={{ background: detailLoadingId === onu.id ? 'rgba(124,58,237,0.28)' : 'rgba(124,58,237,0.15)', border: 'none', borderRadius: 6, padding: '5px 8px', color: '#7c3aed', cursor: detailLoadingId !== null ? 'wait' : 'pointer', opacity: detailLoadingId !== null && detailLoadingId !== onu.id ? 0.55 : 1 }} title='View Detail'>{detailLoadingId === onu.id ? '⏳' : <Eye size={13} />}</button>
                    <button onClick={(event) => { event.stopPropagation(); setEditOnu(onu); }} style={{ background: T.primaryLight, border: 'none', borderRadius: 6, padding: '5px 8px', color: T.primary, cursor: 'pointer' }} title='Edit'><Edit2 size={13} /></button>
                    <button onClick={(event) => { event.stopPropagation(); handleDelete(onu); }} style={{ background: T.errorContainer, border: 'none', borderRadius: 6, padding: '5px 8px', color: T.error, cursor: 'pointer' }} title='Delete'><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ color: T.onSurfaceVar, fontSize: 13 }}>Showing {(page-1)*LIMIT+1}–{Math.min(page*LIMIT,total)} of {total}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}
              style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '6px 12px', color: T.onSurfaceVar, cursor: page===1?'not-allowed':'pointer' }}>
              <ChevronLeft size={14} />
            </button>
            <span style={{ background: T.primary, borderRadius: 8, padding: '6px 14px', color: '#fff', fontSize: 13, fontWeight: 600 }}>{page}</span>
            <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}
              style={{ background: T.surface, border: `1px solid ${T.outlineVar}`, borderRadius: 8, padding: '6px 12px', color: T.onSurfaceVar, cursor: page===totalPages?'not-allowed':'pointer' }}>
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {editOnu && <EditModal onu={editOnu} onClose={() => setEditOnu(null)} onSave={handleSave} speedProfiles={speedProfiles} />}
      {removeJob && <RemovalProgressModal job={removeJob} onClose={() => setRemoveJob(null)} />}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
